import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";

import {
  api,
  ApiError,
  type Camera,
  type CameraProfile,
  type CameraProfileInput,
} from "../api";
import { searchItems } from "../fuzzy";
import { useWords } from "../words";
import { Modal } from "./Modal";

/**
 * Create or edit a camera profile.
 *
 * Lives here rather than inside the Settings section because a profile is
 * something you want the moment you notice you keep picking the same cameras —
 * which is while you are watching them, not while you are in Settings. The
 * scope picker opens this in place on whatever page you are on.
 */
export function CameraProfileDialog({
  profile,
  onClose,
  onSaved,
}: {
  /** null = create a new profile. */
  profile: CameraProfile | null;
  onClose: () => void;
  onSaved?: (saved: CameraProfile) => void;
}) {
  const qc = useQueryClient();
  const w = useWords();
  const cameras = useQuery({
    queryKey: ["cameras", "all"],
    queryFn: () => api.cameras.list(),
  });
  const stores = useQuery({ queryKey: ["stores"], queryFn: api.stores.get });
  const storeLabel = (name: string) =>
    stores.data?.stores.find((s) => s.name === name)?.display_name || name;

  const [name, setName] = useState(profile?.name ?? "");
  const [description, setDescription] = useState(profile?.description ?? "");
  const [selected, setSelected] = useState<string[]>(
    // Only ids that still resolve: a deleted camera should not be silently
    // re-saved into the profile.
    profile ? profile.members.map((m) => m.camera_id) : [],
  );
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: CameraProfileInput) =>
      profile ? api.profiles.update(profile.id, body) : api.profiles.create(body),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["profiles"] });
      onSaved?.(saved);
      onClose();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : `Could not save the ${w.group}.`),
  });

  const list = cameras.data ?? [];
  const visible = useMemo(
    () => searchItems(list, query, (c) => [c.name, c.id, c.store, storeLabel(c.store ?? "")]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, query, stores.data],
  );

  // Grouped by store so picking "every entrance" across sites is a scan, not a
  // hunt through one long flat list.
  const byStore = useMemo(() => {
    const groups = new Map<string, Camera[]>();
    for (const cam of visible) {
      const key = cam.store ? storeLabel(cam.store) : `No ${w.store}`;
      const arr = groups.get(key) ?? [];
      arr.push(cam);
      groups.set(key, arr);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, w, stores.data]);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const selectedStores = new Set(
    selected
      .map((id) => list.find((c) => c.id === id)?.store)
      .filter((s): s is string => Boolean(s)),
  );

  return (
    <Modal
      onClose={onClose}
      labelledBy="profile-dialog-title"
      panelClassName="w-full max-w-2xl"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({
            name: name.trim(),
            description: description.trim(),
            camera_ids: selected,
          });
        }}
      >
        <h3 id="profile-dialog-title" className="text-lg font-semibold text-white">
          {profile ? `Edit ${profile.name}` : `New ${w.group}`}
        </h3>
        <p className="text-sm text-bf-muted">
          A {w.group} is a named set of cameras you can view as one, even when
          they are at different {w.stores}.
        </p>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-bf-muted">
              Name
            </span>
            <input
              className="bf-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="All entrance cameras"
              required
              autoFocus
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-bf-muted">
              Description (optional)
            </span>
            <input
              className="bf-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Front doors at every site"
            />
          </label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-bf-muted">
              Cameras · {selected.length} selected
              {selectedStores.size > 1 && ` across ${selectedStores.size} ${w.stores}`}
            </span>
            {selected.length > 0 && (
              <button
                type="button"
                className="text-xs text-bf-muted transition hover:text-bf-text"
                onClick={() => setSelected([])}
              >
                Clear selection
              </button>
            )}
          </div>

          <label className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2">
            <Search size={15} className="shrink-0 text-bf-muted" />
            <input
              className="w-full bg-transparent text-sm text-bf-text outline-none placeholder:text-bf-muted"
              placeholder={`Search cameras by name or ${w.store}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search cameras"
            />
          </label>

          <div className="max-h-72 space-y-3 overflow-y-auto rounded-lg border border-white/10 bg-white/[0.02] p-3">
            {byStore.length === 0 && (
              <p className="py-4 text-center text-sm text-bf-muted">
                {list.length === 0
                  ? "No cameras configured yet."
                  : "No camera matches that search."}
              </p>
            )}
            {byStore.map(([store, cams]) => (
              <div key={store}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-bf-muted/80">
                  {store}
                </p>
                <div className="space-y-0.5">
                  {cams.map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm transition hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-bf-violet"
                        checked={selected.includes(c.id)}
                        onChange={() => toggle(c.id)}
                      />
                      <span className="min-w-0 flex-1 truncate text-bf-text">
                        {c.name || c.id}
                      </span>
                      {(c.role ?? "ai") === "view_only" && (
                        <span className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-xs text-bf-muted">
                          Non-AI
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="bf-button bg-white/10 bg-none" onClick={onClose}>
            Cancel
          </button>
          <button
            className="bf-button disabled:opacity-40"
            type="submit"
            disabled={save.isPending || !name.trim()}
          >
            {save.isPending ? "Saving…" : profile ? "Save" : `Create ${w.group}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
